import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TableContainer,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { axiosInstance, useClientInfo } from "../utils/commonImports";
import MyDatePicker from "../components/MyDatePicker";

/* =========================
   HELPERS
========================= */
const fmtDate = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "");
const n = (v) => Number(v || 0);

const groupBy = (rows, key) =>
  rows.reduce((acc, r) => {
    const k = r[key] || "UNKNOWN";
    if (!acc[k]) acc[k] = [];
    acc[k].push(r);
    return acc;
  }, {});

/* =========================
   CONFIG MAP
========================= */
const viewConfig = {
  TRIPSHEET: { key: null, label: "Tripsheet Wise" },
  CUSTOMER: { key: "customer_name", label: "Customer Wise" },
  VEHICLE: { key: "vehicle_display", label: "Vehicle/Vendor Wise" },
  VG: { key: "assigned_vehicle_group_name", label: "Vehicle Group Wise" },
};

/* =========================
   COMPONENT
========================= */
export default function ComparisonReport() {
  const navigate = useNavigate();
  const { clientId } = useClientInfo();

  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("STATEMENT");
  const [view, setView] = useState("TRIPSHEET");
  const [filterValue, setFilterValue] = useState("ALL");
  const [subGroup, setSubGroup] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  /* =========================
     BACK BUTTON
     Goes to the previous page. If there is no history
     (page opened directly), goes to the Reports dashboard.
  ========================= */
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/reports");
    }
  };

  /* =========================
     LOAD DATA
  ========================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/tripsheets/report", {
        params: { client_id: clientId },
      })
      .then((res) => setRows(res.data || []))
      .catch(console.error);
  }, [clientId]);

  const subGroupOptions = {
    CUSTOMER: [
      { value: "VEHICLE", label: "Vehicle/Vendor Wise" },
      { value: "VG", label: "Vehicle Group Wise" },
    ],
    VEHICLE: [
      { value: "CUSTOMER", label: "Customer Wise" },
      { value: "VG", label: "Vehicle Group Wise" },
    ],
    VG: [
      { value: "CUSTOMER", label: "Customer Wise" },
      { value: "VEHICLE", label: "Vehicle/Vendor Wise" },
    ],
  };

  /* =========================
     FILTER OPTIONS
  ========================= */
  const key = viewConfig[view].key;

  const options = !key
    ? []
    : ["ALL", ...Array.from(new Set(rows.map((r) => r[key]).filter(Boolean)))];

  /* =========================
     FILTERED DATA
  ========================= */
  const filteredRows = rows.filter((r) => {
    // VIEW FILTER
    if (key && filterValue !== "ALL" && r[key] !== filterValue) return false;

    // DATE FILTER
    if (fromDate && dayjs(r.open_date).isBefore(dayjs(fromDate), "day"))
      return false;

    if (toDate && dayjs(r.open_date).isAfter(dayjs(toDate), "day"))
      return false;

    return true;
  });

  /* =========================
     SUMMARY CALC
  ========================= */
  const calc = (data) => {
    const trips = data.length;
    const sales = data.reduce((t, r) => t + n(r.customer_total), 0);
    const purchase = data.reduce((t, r) => t + n(r.vendor_total), 0);
    const diff = sales - purchase;
    const pc = sales ? ((diff / sales) * 100).toFixed(2) : "0.00";
    return { trips, sales, purchase, diff, pc };
  };

  /* =========================
     STATEMENT TABLE
  ========================= */
  const renderStatementTable = (data) => {
    const total = calc(data);

    return (
      <TableContainer
        sx={{
          mb: 3,
          border: "1px solid #bdbdbd",
          borderRadius: "4px",
        }}
      >
        <Table
          size="small"
          sx={{
            borderCollapse: "collapse",
            "& td, & th": {
              border: "1px solid #d0d0d0",
              fontSize: "0.72rem",
              padding: "4px 6px",
            },
          }}
        >
          {/* ================= HEADER ================= */}
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: "#f4f6f8",
                  fontWeight: 700,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell>Tripsheet</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>

              {/* SALES */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                Sales
              </TableCell>

              <TableCell>Vehicle/Vendor</TableCell>

              {/* PURCHASE */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                Purchase
              </TableCell>

              <TableCell align="right">Diff</TableCell>
              <TableCell align="right">Pc%</TableCell>
            </TableRow>
          </TableHead>

          {/* ================= BODY ================= */}
          <TableBody>
            {data.map((r, i) => {
              const sales = n(r.customer_total);
              const purchase = n(r.vendor_total);
              const diff = sales - purchase;
              const pc = sales ? ((diff / sales) * 100).toFixed(2) : "0.00";

              return (
                <TableRow
                  key={i}
                  sx={{
                    backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <TableCell>{r.tripsheet_number}</TableCell>
                  <TableCell>{fmtDate(r.open_date)}</TableCell>
                  <TableCell>{r.customer_name}</TableCell>

                  {/* SALES */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      borderRight: "3px solid #444",
                    }}
                  >
                    {sales.toFixed(2)}
                  </TableCell>

                  <TableCell>{r.vehicle_display}</TableCell>

                  {/* PURCHASE */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      borderRight: "3px solid #444",
                    }}
                  >
                    {purchase.toFixed(2)}
                  </TableCell>

                  <TableCell align="right">{diff.toFixed(2)}</TableCell>
                  <TableCell align="right">{pc}%</TableCell>
                </TableRow>
              );
            })}
          </TableBody>

          {/* ================= FOOTER ================= */}
          <TableFooter>
            <TableRow
              sx={{
                background: "#f9fafb",
                borderTop: "4px double #333",
                "& td": {
                  fontWeight: 700,
                  fontSize: "0.75rem",
                },
              }}
            >
              {/* first 3 columns */}
              <TableCell colSpan={3}>TOTAL</TableCell>

              {/* SALES TOTAL */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                {total.sales.toFixed(2)}
              </TableCell>

              {/* Vehicle column empty */}
              <TableCell />

              {/* PURCHASE TOTAL */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                {total.purchase.toFixed(2)}
              </TableCell>

              <TableCell align="right">{total.diff.toFixed(2)}</TableCell>
              <TableCell align="right">{total.pc}%</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  };

  /* =========================
     SUMMARY TABLE
  ========================= */
  const renderSummary = () => {
    if (!key) return null;

    const baseRows = filteredRows;
    const grouped = groupBy(baseRows, key);
    const grand = calc(baseRows);

    return (
      <TableContainer
        sx={{
          border: "1px solid #999",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <Table
          size="small"
          sx={{
            "& th, & td": {
              border: "1px solid #bbb",
              fontSize: "0.70rem",
              padding: "4px 6px",
            },
          }}
        >
          {/* HEADER */}
          <TableHead>
            <TableRow
              sx={{
                background: "#f2f2f2",
                "& th": {
                  fontWeight: 700,
                  textAlign: "center",
                },
              }}
            >
              <TableCell>{viewConfig[view].label}</TableCell>

              <TableCell align="right">Trips</TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                Sales
              </TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                Purchase
              </TableCell>

              <TableCell align="right">Diff</TableCell>
              <TableCell align="right">Pc%</TableCell>
            </TableRow>
          </TableHead>

          {/* BODY */}
          <TableBody>
            {Object.entries(grouped).map(([name, data], index) => {
              const t = calc(data);

              // ========= NO SUBGROUP =========
              if (!subGroup) {
                return (
                  <TableRow
                    key={name}
                    sx={{
                      background: index % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>

                    <TableCell align="right">{t.trips}</TableCell>

                    <TableCell
                      align="right"
                      sx={{ borderRight: "3px solid #333" }}
                    >
                      {t.sales.toFixed(2)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{ borderRight: "3px solid #333" }}
                    >
                      {t.purchase.toFixed(2)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        color: t.diff < 0 ? "red" : "inherit",
                        fontWeight: 600,
                      }}
                    >
                      {t.diff.toFixed(2)}
                    </TableCell>

                    <TableCell align="right">{t.pc}%</TableCell>
                  </TableRow>
                );
              }

              // ========= WITH SUBGROUP =========
              const subKey = viewConfig[subGroup]?.key;
              const subGrouped = groupBy(data, subKey);

              return (
                <React.Fragment key={name}>
                  {/* GROUP HEADER */}
                  <TableRow sx={{ background: "#eef2f7" }}>
                    <TableCell
                      colSpan={6}
                      sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                    >
                      {name}
                    </TableCell>
                  </TableRow>

                  {/* SUB ROWS */}
                  {Object.entries(subGrouped).map(([subName, subRows], i) => {
                    const st = calc(subRows);

                    return (
                      <TableRow
                        key={subName}
                        sx={{
                          background: i % 2 === 0 ? "#fff" : "#fafafa",
                        }}
                      >
                        <TableCell sx={{ pl: 4 }}>{subName}</TableCell>

                        <TableCell align="right">{st.trips}</TableCell>

                        <TableCell
                          align="right"
                          sx={{ borderRight: "3px solid #333" }}
                        >
                          {st.sales.toFixed(2)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ borderRight: "3px solid #333" }}
                        >
                          {st.purchase.toFixed(2)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            color: st.diff < 0 ? "red" : "inherit",
                            fontWeight: 600,
                          }}
                        >
                          {st.diff.toFixed(2)}
                        </TableCell>

                        <TableCell align="right">{st.pc}%</TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>

          {/* GRAND TOTAL */}
          <TableFooter>
            <TableRow
              sx={{
                background: "#f9fafb",
                borderTop: "4px double #333",

                "& td": {
                  fontWeight: 700,
                  borderTop: "none",
                },
              }}
            >
              <TableCell>GRAND TOTAL</TableCell>

              <TableCell align="right">{grand.trips}</TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                {grand.sales.toFixed(2)}
              </TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                {grand.purchase.toFixed(2)}
              </TableCell>

              <TableCell align="right">{grand.diff.toFixed(2)}</TableCell>

              <TableCell align="right">{grand.pc}%</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  };

  /* =========================
     MAIN RENDER LOGIC
  ========================= */
  const renderReport = () => {
    if (mode === "SUMMARY") return renderSummary();

    if (filterValue === "ALL" && key) {
      const grouped = groupBy(rows, key);

      return Object.entries(grouped).map(([name, data]) => (
        <Box key={name}>
          <Typography fontWeight={700} mt={2} mb={1}>
            {name}
          </Typography>
          {renderStatementTable(data)}
        </Box>
      ));
    }

    return renderStatementTable(filteredRows);
  };

  /* =========================
     UI
  ========================= */
  return (
    <Box sx={{ mb: 1 }}>
      {/* HEADER WITH BACK BUTTON */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate("/reports")}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Comparison Statement
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Comparison Report
          </Typography>
        </Box>
      </Box>

      {/* FILTER TOOLBAR */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1.5,
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "visible",
          pb: 0.5,

          background: "#fafafa",
          border: "1px solid #e0e0e0",
          borderRadius: "6px",
          padding: "10px 12px",

          "& .MuiFormControl-root": {
            margin: 0,
          },
        }}
      >
        {/* FROM DATE */}
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <MyDatePicker
            label="From Date"
            value={fromDate}
            onChange={setFromDate}
            inputStyle={{
              height: 36,
              fontSize: "0.75rem",
              padding: "4px 6px",
            }}
            containerStyle={{
              width: 150,
              marginBottom: 0,
            }}
          />
        </Box>

        {/* TO DATE */}
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <MyDatePicker
            label="To Date"
            value={toDate}
            onChange={setToDate}
            inputStyle={{
              height: 36,
              fontSize: "0.75rem",
              padding: "4px 6px",
            }}
            containerStyle={{
              width: 150,
              marginBottom: 0,
            }}
          />
        </Box>

        {/* MODE */}
        <FormControl
          size="small"
          sx={{
            minWidth: 180,
            "& .MuiInputBase-root": {
              height: 36,
            },
          }}
        >
          <InputLabel>Mode</InputLabel>
          <Select
            value={mode}
            label="Mode"
            onChange={(e) => setMode(e.target.value)}
          >
            <MenuItem value="STATEMENT">Statement</MenuItem>
            <MenuItem value="SUMMARY">Summary</MenuItem>
          </Select>
        </FormControl>

        {/* VIEW */}
        <FormControl
          size="small"
          sx={{
            minWidth: 220,
            "& .MuiInputBase-root": {
              height: 36,
            },
          }}
        >
          <InputLabel>View</InputLabel>
          <Select
            value={view}
            label="View"
            onChange={(e) => {
              setView(e.target.value);
              setFilterValue("ALL");
            }}
          >
            {Object.entries(viewConfig).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* FILTER */}
        {key && (
          <FormControl
            size="small"
            sx={{
              minWidth: 220,
              "& .MuiInputBase-root": {
                height: 36,
              },
            }}
          >
            <InputLabel>Select</InputLabel>
            <Select
              value={filterValue}
              label="Select"
              onChange={(e) => setFilterValue(e.target.value)}
            >
              {options.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* SUB GROUP */}
        {mode === "SUMMARY" && subGroupOptions[view] && (
          <FormControl
            size="small"
            sx={{
              minWidth: 220,
              "& .MuiInputBase-root": {
                height: 36,
              },
            }}
          >
            <InputLabel>Sub Group</InputLabel>
            <Select
              value={subGroup}
              label="Sub Group"
              onChange={(e) => setSubGroup(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {subGroupOptions[view].map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* SELECTED FILTER TITLE */}
      {filterValue !== "ALL" && key && (
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            mt: 1,
            mb: 1,
            color: "#222",
          }}
        >
          {filterValue}
        </Typography>
      )}

      {/* REPORT */}
      <Paper sx={{ p: 2 }}>{renderReport()}</Paper>
    </Box>
  );
}
